import { ReactNode, useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "@mui/material";
import { Menu, X, LogOut, LayoutDashboard, Package, TrendingUp, BarChart3, MessageSquare, Users } from "lucide-react";
import { useAuthenticatedUser } from "../auth/hooks/useAuthenticatedUser";
import { AuthContext } from "../auth/components/provider";
import {
  WorkspaceId,
  routeForPath,
  visibleRoutes,
  workspaces,
} from "./workspace/navigation";
import "./workspace/workspace.css";

const workspaceIcons: Record<WorkspaceId, typeof LayoutDashboard> = { control: LayoutDashboard, inventory: Package, growth: TrendingUp, insights: BarChart3, enquiries: MessageSquare, administration: Users };

export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthenticatedUser();
  const auth = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [signOutError, setSignOutError] = useState(false);
  const routes = visibleRoutes(user?.roles || []);
  const current = routeForPath(location.pathname);
  const available = workspaces.filter((workspace) =>
    routes.some((route) => route.workspace === workspace.id),
  );
  const activeWorkspace = available.find(
    (workspace) => workspace.id === current?.workspace,
  );
  const signOut = async () => {
    try {
      await auth.signOut();
      navigate("/sign-in");
    } catch {
      setSignOutError(true);
    }
  };
  const navigation = (
    <nav aria-label="Workspaces" className="workspace-sidebar-nav">
      {available.map((workspace) => {
        const Icon = workspaceIcons[workspace.id];
        const groupRoutes = routes.filter((route) => route.workspace === workspace.id);
        const active = current?.workspace === workspace.id;
        return <div className="workspace-nav-group" key={workspace.id}>
          <Link className="workspace-nav-heading" to={groupRoutes[0].path}
            data-active={active || undefined} aria-current={active && groupRoutes.length === 1 ? "page" : undefined}
            onClick={() => setDrawerOpen(false)}><Icon size={18} aria-hidden="true" /><span>{workspace.label}</span></Link>
          {active && groupRoutes.length > 1 && groupRoutes.map((route) => (
            <Link key={route.path} to={route.path} onClick={() => setDrawerOpen(false)}
              aria-current={route.path === current?.path || route.path === current?.parent ? "page" : undefined}>
              {route.label}
            </Link>
          ))}
        </div>;
      })}
    </nav>
  );
  return (
    <div className="workspace-shell">
      <a className="workspace-skip" href="#workspace-content">
        Skip to workspace
      </a>
      <header className="workspace-topbar">
        {routes.some((route) => route.path === "/control-center") ? (
          <Link
            className="workspace-brand"
            to="/control-center"
            aria-label="Kaktu Control Center"
          >
            Kaktu
          </Link>
        ) : (
          <span className="workspace-brand">Kaktu</span>
        )}
        <button className="workspace-picker" aria-label="Open navigation" aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}><Menu size={20} aria-hidden="true" /></button>
        <span className="workspace-current-area">{activeWorkspace?.label || "Workspace"}</span>
        <div className="workspace-account">
          {process.env.REACT_APP_GRAPHQL_URL?.includes("prod") && (
            <span className="workspace-environment">Production</span>
          )}
          <span
            className="workspace-identity"
            title={user?.displayName || user?.email || "User"}
          >
            {user?.displayName || user?.email || "User"}
          </span>
          <button
            className="workspace-icon-button"
            aria-label="Sign out"
            onClick={signOut}
          >
            <LogOut size={18} aria-hidden="true" />
          </button>
        </div>
      </header>
      <aside className="workspace-sidebar">{navigation}</aside>
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} slotProps={{ paper: { className: "workspace-dialog workspace-mobile-sidebar" } }}>
        <div className="workspace-mobile-heading"><span className="workspace-brand">Kaktu</span>
          <button className="workspace-icon-button" aria-label="Close navigation" onClick={() => setDrawerOpen(false)}><X size={20} /></button>
        </div>
        {navigation}
      </Drawer>
      <div className="workspace-main">
      {current?.parent && (
        <nav className="workspace-breadcrumb" aria-label="Breadcrumb">
          <Link to={current.parent}>
            {routes.find((route) => route.path === current.parent)?.label}
          </Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{current.label}</span>
        </nav>
      )}
      {signOutError && (
        <p role="alert" className="workspace-error">
          Unable to sign out. Please try again.
        </p>
      )}
      <div id="workspace-content" tabIndex={-1} className="workspace-content">
        {children}
      </div>
      </div>
    </div>
  );
}
